// SPA GS-Hub — lanceur des apps GS. Layout @gs/ui. registerApiAuth DANS le rendu
// (synchrone, pas useEffect). Garde CLIENT (AccessGate via /api/me) = UX ;
// l'autorite reelle est serveur (requireAuth + remap 403 HUB_USER_NOT_PROVISIONED).
// Ecrans partages : GsLoginScreen (login) + AccessGate (refus) — JAMAIS d'ecran bricole.
//
// EXCEPTION ECRITE (gabarit D7 / MASTER V2 §3.4) : PAS de cloche, PAS de feedback.
// Le serveur hub n'a ni cloche ntf.* ni fbk.* — le cluster chrome se masquerait
// (capability-aware) mais on ne le monte pas. /api/me est le minimum pour que
// AccessGate notProvisioned soit atteignable apres fermeture de provisionOnMiss.
// Seul ntf.* monte : le reglage du canal de l avis (page « Mes parametres », mig 271).
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  GsLayout, GsHeader, GsSidebar, GsMobileBottomNav, GsMoreMenuSheet, GS_MORE_PATH, GsLoginScreen, AccessGate,
  BrandingProvider, GsErrorPage, GsErrorBoundary, GsVersionBanner, useDarkMode,
} from '@gs/ui';
import type { SidebarItem, AccessGateKind } from '@gs/ui';
import { getConfig, GS_APP_URLS } from '@gs/config/browser';
import {
  LayoutDashboard,
  LayoutGrid,
  BarChart2,
  BookOpen,
  Menu,
  Settings,
} from 'lucide-react';
import { useAuth, GsRole } from '@gs/auth';
import { registerApiAuth, clearApiAuth } from '@gs/api-client';
import { useTranslation } from './i18n';
import { useMe } from './hooks/useMe';
import { resolveAccessGate } from './lib/resolveAccessGate';

import { PwaUpdateBanner } from './components/PwaUpdateBanner';
import { ALL_APPS } from './data/apps';
import DashboardPage from './pages/DashboardPage';
import AppsPage from './pages/AppsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import UsersPage from './pages/UsersPage';
import MesParametresPage from './pages/MesParametresPage';

const HUB_APP_NAME = 'GS-Hub';

function hubSectionTitle(pathname: string, t: (key: string) => string): string | null {
  if (pathname === '/' || pathname === '') return t('hub:pageTitle.home');
  if (pathname.startsWith('/apps')) return t('hub:pageTitle.apps');
  if (pathname.startsWith('/analytique')) return t('hub:pageTitle.analytics');
  // PAS de `/docs` : « Documentation » sort du lanceur vers le vrai site
  // (`GS_APP_URLS.docs`), il n'y a plus de route interne a titrer.
  if (pathname.startsWith('/utilisateurs')) return t('hub:pageTitle.users');
  if (pathname.startsWith('/parametres')) return t('hub:pageTitle.settings');
  return null;
}

/**
 * Titre de l'onglet. Le suffixe d'app n'est PAS decoratif : sans lui, l'accueil
 * de hub, de gest et d'inv s'appellent tous les trois « Accueil » et dix onglets
 * GS ouverts redeviennent indistinguables — le defaut qu'on repare
 * (E-20260831-titre-page-fige).
 */
function hubPageTitle(pathname: string, t: (key: string) => string): string {
  const section = hubSectionTitle(pathname, t);
  return section ? `${section} — ${HUB_APP_NAME}` : HUB_APP_NAME;
}

function gsRoleLabel(role: GsRole, t: (key: string) => string): string {
  switch (role) {
    case GsRole.ADMIN:
      return t('hub:roles.admin');
    case GsRole.GESTIONNAIRE:
      return t('hub:roles.manager');
    default:
      return t('hub:roles.user');
  }
}

/**
 * Textes de l'ecran de porte, par cause — refus ET accueil. Les cles sont
 * LITTERALES a dessein : une cle construite (t(`...${kind}`)) echappe a
 * i18next-parser, qui la PURGE ensuite du catalogue (keepRemoved:false).
 * Un cas, une cle ecrite.
 */
function accessGateLabels(kind: AccessGateKind, t: (key: string) => string): { title: string; description: string; retry?: string } {
  switch (kind) {
    case 'accessPending':
      // ADR-076 D3 — l'ACCUEIL, pas un refus : l'etat (« votre compte est
      // reconnu »), la suite, et a qui s'adresser.
      return {
        title: t('hub:accessGate.accessPending.title'),
        description: t('hub:accessGate.accessPending.description'),
      };
    case 'notProvisioned':
      return {
        title: t('hub:accessGate.notProvisioned.title'),
        description: t('hub:accessGate.notProvisioned.description'),
      };
    case 'moduleNotEntitled':
      return {
        title: t('hub:accessGate.moduleNotEntitled.title'),
        description: t('hub:accessGate.moduleNotEntitled.description'),
      };
    case 'unverifiable':
      return {
        title: t('hub:accessGate.unverifiable.title'),
        description: t('hub:accessGate.unverifiable.description'),
        retry: t('hub:accessGate.retry'),
      };
    default:
      return {
        title: t('hub:accessGate.noRole.title'),
        description: t('hub:accessGate.noRole.description'),
      };
  }
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const { user, isLoading, login, logout, getAccessToken } = useAuth();
  const { t } = useTranslation();

  const [moreOpen, setMoreOpen] = useState(false);

  const navItems: SidebarItem[] = [
    { key: 'dash', label: t('hub:nav.home'), icon: LayoutDashboard, path: '/' },
    { key: 'apps', label: t('hub:nav.apps'), icon: LayoutGrid, path: '/apps', badge: ALL_APPS.length },
    { key: 'analytics', label: t('hub:nav.analytics'), icon: BarChart2, path: '/analytique' },
    // « Documentation » SORT du lanceur. La route interne `/docs` ne rendait
    // qu'un gabarit « bientot disponible » — jamais de contenu — alors que le
    // site existe et repond (200 en 0,42 s, mesure du 2026-09-15 a 02 h 05 UTC).
    // Meme mecanisme de sortie que le retour au lanceur d'inv, ug et gest :
    // `externalUrl` du socle, adresse prise au registre unique.
    { key: 'docs', label: t('hub:nav.docs'), icon: BookOpen, path: GS_APP_URLS.docs, externalUrl: true },
    // Le reglage de la personne (canal de l avis, mig 271). Sur mobile, dans « Plus ».
    { key: 'settings', label: t('hub:nav.settings'), icon: Settings, path: '/parametres' },
  ];

  const HUB_MOBILE_PRIMARY = new Set(['dash', 'apps']);
  const mobileNavItems: SidebarItem[] = [
    ...navItems.filter((i) => HUB_MOBILE_PRIMARY.has(i.key)),
    { key: 'more', label: t('hub:nav.more'), icon: Menu, path: GS_MORE_PATH },
  ];
  const moreItems: SidebarItem[] = navItems.filter((i) => !HUB_MOBILE_PRIMARY.has(i.key));

  // PAS de section Administration dans le bandeau. L'item « GS-Admin » qui s'y
  // trouvait doublait la tuile `adm` de la grille (`data/apps.ts`) : meme adresse
  // (`GS_APP_URLS.admin`), meme garde (`hasRole(ADMIN)`), meme ecran. Deux chemins
  // vers une seule destination, et rien n'indiquait lequel etait le bon. On garde
  // le plus riche : la tuile ajoute `gs_hint` pour le SSO silencieux
  // (`buildAppOpenUrl`), le bandeau ouvrait l'adresse nue (arbitrage 2026-09-15).
  // Le mecanisme du socle `SidebarItem.externalUrl` n'est PAS retire — inv, ug et
  // gest s'en servent pour le retour au lanceur.

  if (user) {
    registerApiAuth(getAccessToken);
  } else {
    clearApiAuth();
  }
  useEffect(() => () => { clearApiAuth(); }, []);

  const me = useMe(!!user);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <GsLoginScreen
        appName="Groupe Signalisation"
        logoSrc="/logo-gs.png"
        logoSrcDark="/logo-gs-white.png"
        message={t('hub:login.message')}
        loginLabel={t('hub:login.cta')}
        onLogin={() => void login()}
      />
    );
  }

  // Anti-flash : tant que /api/me n'a pas repondu, on n'affiche NI le shell NI le
  // refus (les deux seraient a retirer). Un spinner, puis UN seul rendu definitif.
  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const gate = resolveAccessGate(me);
  if (gate) {
    return (
      <AccessGate
        kind={gate}
        email={user.email}
        onLogout={() => void logout()}
        onRetry={gate === 'unverifiable' ? () => { void me.refetch(); } : undefined}
        retrying={me.isFetching}
        labels={{
          ...accessGateLabels(gate, t),
          logout: t('hub:chrome.logout'),
        }}
      />
    );
  }

  return (
    // Theming runtime ADR-059 : provider CLIENT seulement. /api/branding n'est
    // pas monte sur hub (exception D7, pas de SQL hors /api/me) -> 404 /api/*
    // existant -> fail-soft = fallback :root GS 2026.
    <BrandingProvider enabled={!!user}>
      <GsLayout
        mainId="contenu-principal"
        skipLinkLabel={t('hub:chrome.skipToContent')}
        header={<GsHeader pageTitle={hubPageTitle(location.pathname, t)} />}
        sidebar={
          <GsSidebar
            variant="light"
            headerLogoSrc="/logo-gs.png"
            headerLogoSrcDark="/logo-gs-white.png"
            homeLabel={t('hub:chrome.backToHome')}
            appName="GS-Hub"
            asideLabel={t('hub:chrome.sidebarAria')}
            navLabel={t('hub:chrome.navAria')}
            mainSectionLabel={t('hub:nav.mainSection')}
            items={navItems}
            activePath={location.pathname}
            onNavigate={(path: string) => navigate(path)}
            LinkComponent={Link}
            bottomUser={{
              name: user.name,
              email: user.email,
              role: gsRoleLabel(user.role, t),
              initials: user.initials,
            }}
            isDark={isDark}
            onToggleDark={toggleDark}
            onLogout={() => void logout()}
            appVersion="0.1.0"
            collapseLabel={t('hub:chrome.collapseSidebar')}
            expandLabel={t('hub:chrome.expandSidebar')}
            userMenuLabels={{
              lightMode: t('hub:chrome.lightMode'),
              darkMode: t('hub:chrome.darkMode'),
              language: t('hub:chrome.language'),
              logout: t('hub:chrome.logout'),
            }}
          />
        }
        mobileNav={
          <GsMobileBottomNav
            items={mobileNavItems}
            activePath={location.pathname}
            onNavigate={(path: string) => {
              if (path === GS_MORE_PATH) { setMoreOpen(true); return; }
              navigate(path);
            }}
            navLabel={t('hub:nav.mobileNav')}
          />
        }
      >
        <GsVersionBanner apiBaseUrl={getConfig().apiUrl} />
        <PwaUpdateBanner />
        <GsErrorBoundary>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/apps" element={<AppsPage />} />
            {/* PlaceholderPage reste monte ici : `/analytique` l'atteint encore.
                Seule la route `/docs` est retiree — sa destination est desormais
                hors du lanceur. Une adresse `/docs` restee dans un favori tombe
                sur la redirection `*` vers l'accueil. */}
            <Route path="/analytique" element={<PlaceholderPage title={t('hub:pageTitle.analytics')} />} />
            <Route path="/utilisateurs" element={<UsersPage />} />
            <Route path="/parametres" element={<MesParametresPage />} />
            <Route path="/error/:code" element={<GsErrorPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </GsErrorBoundary>
      </GsLayout>
      <GsMoreMenuSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={moreItems}
        onNavigate={(path: string) => navigate(path)}
        title={t('hub:nav.more')}
        closeLabel={t('hub:nav.closeMore')}
        onLogout={() => void logout()}
        logoutLabel={t('hub:chrome.logout')}
      />
    </BrandingProvider>
  );
}
